import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import Modal, {
  TextField,
  TextArea,
  SelectField,
  PrimaryButton,
  SecondaryButton,
} from "@/components/inventory/Modal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  sku: "",
  name: "",
  description: "",
  category_id: "",
  uom: "PCS",
  hsn_code: "",
  barcode: "",
  reorder_level: "",
};

export default function ProductsPage() {
  const { user } = useAuth();
  const canWrite = user && ["admin", "manager"].includes(user.role);

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/inventory/products", { params: { q: q || undefined } }),
        api.get("/inventory/categories", { params: { is_active: true } }),
      ]);
      setItems(pRes.data);
      setCategories(cRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({
      sku: row.sku,
      name: row.name,
      description: row.description || "",
      category_id: row.category_id || "",
      uom: row.uom || "PCS",
      hsn_code: row.hsn_code || "",
      barcode: row.barcode || "",
      reorder_level: row.reorder_level ?? "",
    });
    setOpen(true);
  };

  const buildPayload = () => {
    const p = {
      name: form.name.trim(),
      description: form.description || null,
      category_id: form.category_id || null,
      uom: form.uom.trim().toUpperCase(),
      hsn_code: form.hsn_code || null,
      barcode: form.barcode || null,
      reorder_level:
        form.reorder_level === "" ? null : Number(form.reorder_level),
    };
    if (!editing) p.sku = form.sku.trim().toUpperCase();
    return p;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await api.put(`/inventory/products/${editing.id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/inventory/products", payload);
        toast.success("Product created.");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Deactivate product "${row.sku}"?`)) return;
    try {
      await api.delete(`/inventory/products/${row.id}`);
      toast.success("Product deactivated.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <InventoryLayout
      title="Products"
      right={
        canWrite && (
          <PrimaryButton testid="prod-new-btn" onClick={startCreate}>
            + New product
          </PrimaryButton>
        )
      }
    >
      <div className="mb-4 p-3 border border-slate-200 bg-slate-50 text-xs text-slate-600 leading-relaxed">
        <strong className="uppercase tracking-[0.15em] text-slate-800">Business rule.</strong>{" "}
        Product master carries no purchase or selling price. Prices belong
        exclusively to transaction line items (Purchase, GRN, Sale, Invoice).
      </div>

      <div className="mb-6 flex gap-3 max-w-md">
        <input
          data-testid="prod-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search SKU, name or barcode…"
          className="flex-1 h-11 px-3 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
        />
        <SecondaryButton testid="prod-search-btn" onClick={load}>Search</SecondaryButton>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">SKU</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Name</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Category</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">UOM</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">HSN</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Reorder</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Status</th>
              <th className="px-4 py-3 w-40" />
            </tr>
          </thead>
          <tbody data-testid="prod-tbody">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">No products yet.</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`prod-row-sku-${row.id}`}>{row.sku}</td>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3 text-slate-600">{row.category_id ? catName(row.category_id) : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.uom}</td>
                  <td className="px-4 py-3 text-slate-600">{row.hsn_code || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.reorder_level ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${row.is_active ? "text-[#039855]" : "text-slate-400"}`}>
                      {row.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex gap-2 justify-end">
                        <button
                          data-testid={`prod-edit-${row.id}`}
                          onClick={() => startEdit(row)}
                          className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 hover:text-[#002FA7]"
                        >
                          Edit
                        </button>
                        {row.is_active && (
                          <button
                            data-testid={`prod-delete-${row.id}`}
                            onClick={() => onDelete(row)}
                            className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D92D20] hover:opacity-80"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        testid="prod"
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit product" : "New product"}
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="SKU"
              testid="prod-form-sku"
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              required
              disabled={!!editing}
              hint={editing ? "SKU cannot be changed." : "Uppercase alphanumeric."}
            />
            <TextField
              label="Name"
              testid="prod-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <TextArea
            label="Description"
            testid="prod-form-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Category"
              testid="prod-form-category"
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            >
              <option value="">— None —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </SelectField>
            <TextField
              label="Unit of measure"
              testid="prod-form-uom"
              value={form.uom}
              onChange={(e) => setForm({ ...form, uom: e.target.value })}
              required
              hint="e.g., PCS, KG, LTR, BOX."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="HSN code"
              testid="prod-form-hsn"
              value={form.hsn_code}
              onChange={(e) => setForm({ ...form, hsn_code: e.target.value })}
            />
            <TextField
              label="Barcode"
              testid="prod-form-barcode"
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
            />
          </div>

          <TextField
            label="Reorder level"
            testid="prod-form-reorder"
            type="number"
            min="0"
            step="0.01"
            value={form.reorder_level}
            onChange={(e) => setForm({ ...form, reorder_level: e.target.value })}
            hint="Total across warehouses. Leave blank to disable low-stock alerts."
          />

          <p className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
            No price fields. Prices live on transactions (Purchase, Sale, GRN, Invoice).
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton testid="prod-form-cancel" type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton testid="prod-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save changes" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </InventoryLayout>
  );
}
